<template>
  <b-modal
    :id="modalId"
    :title="modalTitle"
    size="lg"
    no-close-on-esc
    no-close-on-backdrop
  >
    <div v-if="task">
      <b-card v-if="isFormTask" class="mb-3" no-body>
        <b-card-header class="pb-1">
          <h5 class="mb-0 text-capitalize">{{ $t("unit.task_type_form") }}</h5>
        </b-card-header>
        <b-table-simple responsive bordered class="mb-0">
          <b-tbody>
            <b-tr>
              <b-th class="table-header text-capitalize" style="width: 30%">
                {{ $t("unit.name") }}
              </b-th>
              <b-td>
                <div v-if="formLinkId" class="d-flex align-items-center">
                  <span
                    @click="goToForm(task)"
                    class="entity-link mr-1"
                  >
                    {{ formDisplayName }}
                  </span>
                  <feather-icon
                    icon="ExternalLinkIcon"
                    size="16"
                    class="cursor-pointer text-primary"
                    @click="goToForm(task)"
                  />
                </div>
                <span v-else>{{ formDisplayName }}</span>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-card>

      <b-card v-else-if="!isAlarmTask" class="mb-3" no-body>
        <b-card-header class="pb-1">
          <h5 class="mb-0 text-capitalize">{{ $t("unit.single") }}</h5>
        </b-card-header>
        <b-table-simple responsive bordered class="mb-0">
          <b-tbody>
            <b-tr>
              <b-th class="table-header text-capitalize" style="width: 30%">
                {{ $t("unit.description") }}
              </b-th>
              <b-td>
                <div v-if="task.unit && task.unit.id" class="d-flex align-items-center">
                  <span
                    @click="goToUnit(task.unit.id)"
                    class="entity-link mr-1"
                  >
                    {{ task.unit.description || "N/A" }}
                  </span>
                  <feather-icon
                    icon="ExternalLinkIcon"
                    size="16"
                    class="cursor-pointer text-primary"
                    @click="goToUnit(task.unit.id)"
                  />
                </div>
                <span v-else>N/A</span>
              </b-td>
            </b-tr>
            <b-tr v-if="task.unit && task.unit.category">
              <b-th class="table-header text-capitalize">{{ $t("unit.category") }}</b-th>
              <b-td>{{ task.unit.category.name }}</b-td>
            </b-tr>
            <b-tr v-if="task.unit && task.unit.router && task.unit.router.tenant">
              <b-th class="table-header text-capitalize">Tenant</b-th>
              <b-td>{{ task.unit.router.tenant.name }}</b-td>
            </b-tr>
            <b-tr v-if="task.unit && task.unit.region">
              <b-th class="table-header text-capitalize">{{ $t("region.title").split('|')[0].trim() }}</b-th>
              <b-td>{{ task.unit.region.name }}</b-td>
            </b-tr>
            <b-tr v-if="task.unit && task.unit.country">
              <b-th class="table-header text-capitalize">{{ $t("country.title").split('|')[0].trim() }}</b-th>
              <b-td>{{ task.unit.country.name }}</b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-card>

      <b-card v-if="isAlarmTask" class="mb-3" no-body>
        <b-card-header class="pb-1">
          <h5 class="mb-0 text-capitalize">{{ $t("alarm.title") }}</h5>
        </b-card-header>
        <b-table-simple responsive bordered class="mb-0">
          <b-tbody>
            <b-tr>
              <b-th class="table-header text-capitalize" style="width: 30%">
                ID
              </b-th>
              <b-td>
                <div v-if="hasAlarmReference" class="d-flex align-items-center">
                  <span class="entity-link mr-1" @click="openAlarmFromTask">
                    #{{ alarmReferenceId }}
                  </span>
                  <feather-icon
                    icon="ExternalLinkIcon"
                    size="16"
                    class="cursor-pointer text-primary"
                    @click="openAlarmFromTask"
                  />
                </div>
                <span v-else>N/A</span>
              </b-td>
            </b-tr>
          </b-tbody>
        </b-table-simple>
      </b-card>

      <!-- Comment Section -->
      <b-card class="mb-3" no-body>
        <b-card-header class="pb-1">
          <h5 class="mb-0 text-capitalize">{{ $t("unit.disable_comment") }}</h5>
        </b-card-header>
        <b-card-body>
          <p class="mb-0 preserve-whitespace">{{ task.comment || "N/A" }}</p>
        </b-card-body>
      </b-card>

      <!-- Status and Date Table -->
      <b-table-simple responsive bordered>
        <b-tbody>
          <b-tr>
            <b-th class="table-header text-capitalize" style="width: 30%">
              {{ $t("actions.status") }}
            </b-th>
            <b-td>
              <b-badge :variant="statusVariant">
                {{ $t(`unit.task_status.${task.status}`) }}
              </b-badge>
            </b-td>
          </b-tr>
          <b-tr>
            <b-th class="table-header text-capitalize">{{ $t("date.created_at") }}</b-th>
            <b-td>{{ formatDate(task.created_at) }}</b-td>
          </b-tr>
        </b-tbody>
      </b-table-simple>
    </div>

    <template #modal-footer>
      <b-button
        variant="secondary"
        @click="$bvModal.hide(modalId)"
        :disabled="loading"
        class="text-capitalize"
      >
        {{ $t("actions.cancel") }}
      </b-button>
      <b-button
        v-if="isAlarmTask && hasAlarmReference"
        variant="primary"
        @click="openAlarmFromTask"
        :disabled="loading || openingAlarm"
        class="text-capitalize"
      >
        <b-spinner v-if="openingAlarm" small class="mr-50" />
        <feather-icon v-else icon="ExternalLinkIcon" class="mr-50" />
        Alarma
      </b-button>
      <b-button
        v-if="isAlarmTask && canOpenLinkedForm"
        variant="info"
        @click="openLinkedFormFromTask"
        :disabled="loading || openingAlarm"
        class="text-capitalize"
      >
        <feather-icon icon="FileTextIcon" class="mr-50" />
        {{ $t("unit.view_form") }}
      </b-button>
      <b-button
        v-if="task && task.status === 'pending' && (!isFormTask || isNotificationFormTask || isAnsweredFormTask)"
        variant="success"
        @click="markAsDone"
        :disabled="loading"
        class="text-capitalize"
      >
        <b-spinner v-if="loading" small class="mr-50" />
        <feather-icon v-else icon="CheckIcon" class="mr-50" />
        {{ isNotificationFormTask ? "Revisado" : $t("unit.mark_as_done") }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { ref, computed } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import moment from "moment-timezone";

export default {
  props: {
    modalId: {
      type: String,
      default: "view-assigned-task-modal",
    },
    task: {
      type: Object,
      default: null,
    },
  },
  setup(props, context) {
    const { emit } = context;
    const toast = useToast();
    const loading = ref(false);
    const openingAlarm = ref(false);

    const translate = (key, params = {}) => {
      return context.root.$t(key, params);
    };

    const isFormTask = computed(() => {
      return (
        props.task &&
        (props.task.type === "form" ||
          props.task.type === "answered-form" ||
          props.task.type === "answered_form" ||
          props.task.type === "notification-form" ||
          props.task.type === "form-created-with-relation")
      );
    });

    const isNotificationFormTask = computed(() => {
      return props.task?.type === "notification-form";
    });

    const isAnsweredFormTask = computed(() => {
      return (
        props.task?.type === "answered_form" ||
        props.task?.type === "answered-form"
      );
    });

    const isAlarmTask = computed(() => {
      return props.task?.type === "alarm";
    });

    const modalTitle = computed(() => {
      if (!props.task) return translate("unit.assigned_task_details");
      if (props.task.type === "answered_form") return translate("unit.assigned_security_form_task_details");
      if (isFormTask.value) return translate("unit.assigned_form_task_details");
      return translate("unit.assigned_task_details");
    });

    const formDisplayName = computed(() => {
      if (!props.task) return "N/A";
      const task = props.task;
      const name =
        (task.form_answer_bind && task.form_answer_bind.name) ||
        (task.form && task.form.name) ||
        (task.form && task.form.template && task.form.template.name) ||
        (task.template && task.template.name) ||
        (task.form_template && task.form_template.name) ||
        null;
      if (name) return name;
      if (task.id_form) return translate("unit.view_form");
      return "N/A";
    });

    const statusVariant = computed(() => {
      if (!props.task) return "secondary";
      switch (props.task.status) {
        case "pending":
          return "warning";
        case "done":
          return "success";
        case "cancelled":
          return "danger";
        default:
          return "secondary";
      }
    });

    const formatDate = (date) => {
      if (!date) return "N/A";
      return moment(date).format("DD/MM/YYYY HH:mm");
    };

    const goToUnit = (idUnit) => {
      if (idUnit) {
        const route = context.root.$router.resolve({ path: "/unit", query: { editUnitId: idUnit } });
        window.open(route.href, "_blank");
      }
    };

    const formLinkId = computed(() => {
      const taskItem = props.task;
      if (!taskItem) return null;
      return taskItem.id_form_answer ?? taskItem.form?.id ?? taskItem.id_form ?? null;
    });

    const alarmReferenceId = computed(() => {
      const taskItem = props.task;
      if (!taskItem) return null;
      return taskItem.id_alarm ?? taskItem.alarm_id ?? taskItem.alarm?.id ?? null;
    });

    const hasAlarmReference = computed(() => {
      return alarmReferenceId.value != null;
    });

    const canOpenLinkedForm = computed(() => {
      if (!props.task) return false;
      return Boolean(formLinkId.value || props.task.id_template_to_create);
    });

    const goToForm = (taskItem) => {
      if (!taskItem) return;
      const isNotificationForm = taskItem.type === "notification-form";
      const isAlarm = taskItem.type === "alarm";
      const hasAnsweredReference = taskItem.id_form_answer != null || taskItem.form?.id != null || taskItem.id_form != null;
      const isAnsweredForm = taskItem.type === "answered_form" || isNotificationForm || (isAlarm && hasAnsweredReference);
      const path = isNotificationForm
        ? "/operational_security/form-answers-bau"
        : "/operational_security/form-answers";
      const query = isAnsweredForm
        ? { "open-id": String(taskItem.id_form_answer ?? taskItem.form?.id ?? taskItem.id_form ?? "") }
        : { "form-id": String(taskItem.id_template_to_create ?? taskItem.id_form ?? "") };
      if (isAnsweredForm && !query["open-id"]) return;
      if (!isAnsweredForm && !query["form-id"]) return;

      if (taskItem.id_template_to_create) {
        query["owner-form"] = taskItem.id_form
      }

      if (taskItem.id != null && taskItem.type !== "answered_form" && taskItem.type !== "answered-form") {
        query.id_task = String(taskItem.id);
      }
      const alarmId = taskItem.id_alarm ?? taskItem.alarm_id ?? taskItem.alarm?.id ?? null;
      if (alarmId != null) {
        query.id_alarm = String(alarmId);
      }

      const route = context.root.$router.resolve({ path, query });
      window.open(route.href, "_blank");
    };

    const openLinkedFormFromTask = () => {
      if (!props.task) return;
      goToForm(props.task);
    };

    const openAlarmFromTask = async () => {
      if (!props.task) return;
      const alarmId = alarmReferenceId.value;
      if (alarmId == null) {
        toast({
          component: ToastificationContent,
          props: {
            title: translate("messages.error.server_error"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
        return;
      }

      openingAlarm.value = true;
      try {
        emit("open-task-alarm", props.task);
        context.root.$bvModal.hide(props.modalId);
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title: translate("messages.error.server_error"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        openingAlarm.value = false;
      }
    };

    const markAsDone = async () => {
      if (!props.task) return;

      loading.value = true;

      try {
        await store.dispatch("unit/updateTaskStatus", {
          id: props.task.id,
          status: "done",
        });

        toast({
          component: ToastificationContent,
          props: {
            title: translate("messages.success.task_completed"),
            icon: "CheckIcon",
            variant: "success",
          },
        });

        emit("task-updated", props.task.id);

        setTimeout(() => {
          context.root.$bvModal.hide(props.modalId);
        }, 500);
      } catch (error) {
        toast({
          component: ToastificationContent,
          props: {
            title:
              error.response?.data?.message ||
              translate("messages.error.server_error"),
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      } finally {
        loading.value = false;
      }
    };

    return {
      loading,
      openingAlarm,
      isFormTask,
      isNotificationFormTask,
      isAnsweredFormTask,
      isAlarmTask,
      modalTitle,
      formDisplayName,
      formLinkId,
      alarmReferenceId,
      hasAlarmReference,
      canOpenLinkedForm,
      statusVariant,
      formatDate,
      markAsDone,
      goToUnit,
      goToForm,
      openAlarmFromTask,
      openLinkedFormFromTask,
    };
  },
};
</script>

<style scoped>
.text-capitalize {
  text-transform: capitalize;
}

.table-header {
  background-color: #f8f9fa !important;
  color: #6e6b7b !important;
  font-weight: 500;
  padding: 0.75rem;
}

.preserve-whitespace {
  white-space: pre-line;
}

.entity-link {
  cursor: pointer;
  color: #0052cc;
  text-decoration: none;
}

.entity-link:hover {
  text-decoration: underline;
}
</style>

